import { CloseOutlined } from "@ant-design/icons";
import { NOTIFICATION_BANNER_CLOSED_KEY } from "../../Utils/storage-constants";
import useAccess from "../../Hooks/useAccess";
import { formatPlanName } from "../../Utils/planUtils";
import { getRemainingDays } from "../../Utils/generic-functions";
import "./NotificationBanner.scss";

interface NotificationBannerProps {
  isVisible: boolean;
  onClose: () => void;
}

const NotificationBanner = ({
  isVisible,
  onClose,
}: NotificationBannerProps) => {
  const { currentSubscription, currentMode, currentLimits, currentPlan } =
    useAccess();

  const handleClose = () => {
    try {
      localStorage.setItem(NOTIFICATION_BANNER_CLOSED_KEY, "true");
      onClose();
    } catch {
      onClose();
    }
  };

  if (
    !isVisible ||
    !currentSubscription ||
    !currentMode ||
    !currentLimits ||
    !currentPlan
  ) {
    return null;
  }

  const remainingAssessments =
    currentLimits.applicationsProcessed -
    currentSubscription.measurements.applicationsProcessed;

  const currentPlanName = formatPlanName(currentPlan.name);

  const expiryRemainingDays = getRemainingDays(currentSubscription.endDate);

  const isExpiringSoon = expiryRemainingDays <= 3;

  const isExpired = currentSubscription.status === "inactive";

  const getMessage = (): string | null => {
    if (isExpired) {
      return `Your ${currentPlanName} has expired. Renew now to continue posting jobs and processing applications.`;
    }
    if (remainingAssessments <= 0) {
      return `You've reached your assessment limit on the ${currentPlanName}. Upgrade your plan to keep processing new assessments.`;
    }
    if (isExpiringSoon) {
      return `Your ${currentPlanName} expires in ${expiryRemainingDays} day${expiryRemainingDays === 1 ? "" : "s"}. Renew now to avoid any interruption.`;
    }

    return null;
  };

  const message = getMessage();
  const isValidMessage = message != null && message.trim() !== "";

  if (!isValidMessage) {
    localStorage.setItem(NOTIFICATION_BANNER_CLOSED_KEY, "true");
    onClose();
    return null;
  }

  return (
    <div className="notification-banner">
      <span className="notification-banner__text">{message}</span>
      <button
        type="button"
        className="notification-banner__close"
        onClick={handleClose}
        aria-label="Close banner"
      >
        <CloseOutlined />
      </button>
    </div>
  );
};

export default NotificationBanner;
